import React, { useState } from 'react';
import { LedgerEntry } from './components/LedgerEntry';
import { ResultsOverlay } from './components/ResultsOverlay';
import { BrandFooter } from './components/BrandFooter';
import { createBlocks, LOGO_URL, TOTAL_BLOCKS, type Block, type BlockStatus } from './utils/blocks';
export function App() {
  const [blocks, setBlocks] = useState<Block[]>(createBlocks);
  const [showResults, setShowResults] = useState(false);
  const completados = blocks.filter(b => b.status).length;
  const crecH = blocks.filter(b => b.status === 'crecimiento').length * 0.5;
  const fugaH = blocks.filter(b => b.status === 'fuga').length * 0.5;
  const fill = `${(completados / TOTAL_BLOCKS * 100).toFixed(0)}%`;
  const handleActivityChange = (id: number, value: string) => {
    setBlocks(prev => prev.map(b => b.id === id ? {
      ...b,
      activity: value
    } : b));
  };
  const handleToggle = (id: number, status: Exclude<BlockStatus, null>) => {
    setBlocks(prev => prev.map(b => b.id === id ? {
      ...b,
      status: b.status === status ? null : status
    } : b));
  };
  const handleReset = () => {
    if (window.confirm('¿Borrar todo el registro y empezar de nuevo?')) {
      setBlocks(createBlocks());
      setShowResults(false);
      window.scrollTo(0, 0);
    }
  };
  const completo = completados >= TOTAL_BLOCKS;
  return <div className="w-full min-h-full" data-id="element-0" data-mp="2zlldgx6hkckm">
      <header className="hero" style={{
      ['--fill' as string]: fill
    } as React.CSSProperties} data-id="element-1" data-mp="2zmo5j8fjol24">
        <img className="brand-logo" src={LOGO_URL} alt="Carolina Gómez" data-id="element-2" data-mp="33orjc3pos1qk" />
        <p className="eyebrow" data-id="element-3" data-mp="38k6tolohjfi8">Mapa de Fugas · Diagnóstico</p>
        <h1 data-id="element-4" data-mp="3dle28mygcipy">¿Por qué tu esfuerzo no se refleja en tu crecimiento?</h1>
        <p data-id="element-5" data-mp="3dii530ufhy2k">
          No necesitas más tiempo, sino dejar de perder el que ya tienes. Este
          diagnóstico te muestra en qué se te está yendo el tiempo, la energía y
          el foco — para que veas, con números, lo que hoy no estás viendo.
        </p>
        <div className="hero-stats" data-id="element-6" data-mp="356s95xbri9l">
          <div data-id="element-7" data-mp="38b6yx8f1wei">
            <span className="n" data-id="element-8" data-mp="3ud1glcmeruc7">
              {completados}/{TOTAL_BLOCKS}
            </span>
            <span className="l" data-id="element-9" data-mp="d4o0nv3yoa99">Bloques</span>
          </div>
          <div data-id="element-10" data-mp="d3wd97kipiu2">
            <span className="n" data-id="element-11" data-mp="d2l6qzzotwz9">{crecH.toFixed(1)}h</span>
            <span className="l" data-id="element-12" data-mp="86mgf6usu4w7">Sumó</span>
          </div>
          <div data-id="element-13" data-mp="3glea6jyr55fp">
            <span className="n" data-id="element-14" data-mp="3gkv2rx6q9zfx">{fugaH.toFixed(1)}h</span>
            <span className="l" data-id="element-15" data-mp="3gja6o34rm8fy">No sumó</span>
          </div>
        </div>
      </header>

      <main data-id="element-16" data-mp="3bo4mp9uxb3ui">
        <section className="section-wrap" data-id="element-17" data-mp="3bk72sm0iu89k">
          <div className="intro-block" data-id="element-18" data-mp="3qbfwb67jodrz">
            <p className="section-label" data-id="element-19" data-mp="3qciodhglsm9h">Antes de empezar</p>
            <p data-id="element-20" data-mp="3lk7k62etyeb4">
              La mayoría de las personas que hacen este diagnóstico llegan
              pensando que su problema es el tiempo. No lo es: en el 100% de los
              casos que hemos revisado, hay horas —muchas veces varias al día—
              que ya tienes disponibles, pero que no están sumando a tu
              crecimiento.
            </p>
          </div>
          <div className="intro-block" data-id="element-21" data-mp="1d47zjnss9f3v">
            <p data-id="element-22" data-mp="1d62i4vgl0v4s">
              Estar ocupada no es lo mismo que estar creciendo. Puedes tener el
              día lleno de actividad y aun así no avanzar, porque no toda
              actividad construye.{' '}
              <b data-id="element-23" data-mp="13ey541agvkcl">Detectar en qué se te está yendo lo que ya tienes</b> es el
              primer paso para recuperarlo.
            </p>
          </div>
          <div className="intro-block" data-id="element-24" data-mp="1w3vknwnan31n">
            <p data-id="element-25" data-mp="1w2u091ke1479">
              Este diagnóstico no te va a decir qué hacer con tu tiempo. Te va a
              mostrar, con números y sin filtros, algo que probablemente no
              puedes ver desde adentro: en qué se te está yendo lo que ya
              tienes.
            </p>
          </div>

          <div className="instrucciones" data-id="element-26" data-mp="6muln4y02mk8">
            <p className="section-label" data-id="element-27" data-mp="apr7d4iu6yse">Cómo funciona</p>
            <p data-id="element-28" data-mp="arc1wu55ihq9">
              Vas a registrar el día de <b data-id="element-29" data-mp="qwvqqbxgej5b">ayer</b>, bloque por bloque, de
              6:00am a 10:00pm — tal como ocurrió, sin maquillarlo. En cada
              bloque anota qué hiciste y marca si esta actividad te generó o no
              crecimiento real. Al final vas a ver, en números, cuánto tiempo no
              te sumó.
            </p>
          </div>
        </section>

        <section className="ledger-wrap" data-id="element-30" data-mp="ca2kdw5wwnp9">
          <div className="ledger-head" data-id="element-31" data-mp="19vrhfda3sfee">
            <h2 data-id="element-32" data-mp="19uopd211o6ww">Registro de ayer</h2>
            <span data-id="element-33" data-mp="19tnfv1zofsha">Debe / Haber</span>
          </div>
          <div data-id="element-34" data-mp="14xyauj4w1jdl">
            {blocks.map(b => <LedgerEntry key={b.id} block={b} onActivityChange={handleActivityChange} onToggle={handleToggle} data-id="element-35" data-mp="zxtvwxjmowph" />)}
          </div>
        </section>
      </main>

      <BrandFooter data-id="element-36" data-mp="3kouamzanp640" />

      <div className="barra" data-id="element-37" data-mp="3kpdwgr3fgenb">
        <div className="barra-info" data-id="element-38" data-mp="3gmhaqrilc2f5">
          Completados: <b data-id="element-39" data-mp="3gkwcorx2i1cj">{completados}</b>/{TOTAL_BLOCKS}
        </div>
        <button type="button" className="btn-ver" disabled={!completo} onClick={() => {
        setShowResults(true);
        window.scrollTo(0, 0);
      }} data-id="element-40" data-mp="1w4ylm5b1o21m">
          {completo ? 'Ver mi diagnóstico' : `Faltan ${TOTAL_BLOCKS - completados} bloques`}
        </button>
      </div>

      <ResultsOverlay show={showResults} blocks={blocks} onClose={() => setShowResults(false)} onReset={handleReset} data-id="element-41" data-mp="29xwmg769amck" />
    </div>;
}